import { useState, useEffect } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { 
  Ticket, Plus, Trash2, Loader2, Copy, Check, Building, Calendar,
  Clock, CheckCircle, XCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminTrialCodes = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showUnusedOnly, setShowUnusedOnly] = useState(false);
  const [stats, setStats] = useState({ total: 0, unused: 0 });
  
  // Create form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [customCode, setCustomCode] = useState('');
  const [trialDays, setTrialDays] = useState(30);
  
  // Copy state
  const [copiedCode, setCopiedCode] = useState(null);

  useEffect(() => {
    fetchCodes();
  }, [showUnusedOnly]);

  const fetchCodes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/trial-codes`, {
        params: { show_used: !showUnusedOnly }
      });
      setCodes(response.data.trial_codes);
      setStats({
        total: response.data.total,
        unused: response.data.unused_count
      });
    } catch (error) {
      console.error('Failed to fetch trial codes:', error);
      toast.error('Failed to load trial codes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCode = async () => {
    setCreating(true);
    try {
      const response = await axios.post(`${API}/admin/trial-codes`, {
        code: customCode || null,
        trial_duration_days: trialDays
      });
      toast.success(`Trial code created: ${response.data.trial_code.code}`);
      setShowCreateForm(false);
      setCustomCode('');
      setTrialDays(30);
      fetchCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create trial code');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteCode = async (codeId, code) => {
    if (!window.confirm(`Delete trial code "${code}"? This cannot be undone.`)) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/trial-codes/${codeId}`);
      toast.success('Trial code deleted');
      fetchCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete trial code');
    }
  };

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success('Code copied to clipboard');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Ticket className="w-7 h-7 text-red-400" />
            Trial Codes
          </h1>
          <p className="text-neutral-400 text-sm mt-1">
            Create one-time use codes for free trial subscriptions
          </p>
        </div>
        
        <Button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="bg-red-500 hover:bg-red-600 rounded-full"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Code
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-[#121212] rounded-xl border border-white/10 p-4">
          <div className="flex items-center gap-2 text-neutral-400 text-sm mb-1">
            <Ticket className="w-4 h-4" />
            Total Codes
          </div>
          <p className="text-2xl font-bold text-white">{stats.total}</p>
        </div>
        <div className="bg-[#121212] rounded-xl border border-white/10 p-4">
          <div className="flex items-center gap-2 text-neutral-400 text-sm mb-1">
            <CheckCircle className="w-4 h-4 text-green-400" />
            Unused Codes
          </div>
          <p className="text-2xl font-bold text-green-400">{stats.unused}</p>
        </div>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <div className="bg-[#121212] rounded-xl border border-white/10 p-6 space-y-4">
          <h3 className="text-lg font-semibold text-white">Create New Trial Code</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-neutral-400 mb-2">
                Custom Code (optional)
              </label>
              <Input
                value={customCode}
                onChange={(e) => setCustomCode(e.target.value.toUpperCase())}
                placeholder="Leave empty to auto-generate"
                className="bg-white/5 border-white/10"
              />
              <p className="text-xs text-neutral-500 mt-1">
                4-20 characters, letters, numbers, and hyphens only
              </p>
            </div>
            
            <div>
              <label className="block text-sm text-neutral-400 mb-2">
                Trial Duration (days)
              </label>
              <Input
                type="number"
                value={trialDays}
                onChange={(e) => setTrialDays(parseInt(e.target.value) || 30)}
                min={1}
                max={365}
                className="bg-white/5 border-white/10"
              />
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              onClick={handleCreateCode}
              disabled={creating}
              className="bg-green-600 hover:bg-green-700 rounded-full"
            >
              {creating ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Create Code
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setShowCreateForm(false);
                setCustomCode('');
                setTrialDays(30);
              }}
              className="text-neutral-400 hover:text-white"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Filter Toggle */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setShowUnusedOnly(!showUnusedOnly)}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
            showUnusedOnly 
              ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
              : 'bg-white/5 text-neutral-400 border border-white/10 hover:text-white'
          }`}
        >
          {showUnusedOnly ? 'Showing Unused Only' : 'Show All Codes'}
        </button>
      </div>

      {/* Codes List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-red-500" />
        </div>
      ) : codes.length === 0 ? (
        <div className="text-center py-12 text-neutral-400">
          <Ticket className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No trial codes found</p>
          <p className="text-sm mt-1">Create your first trial code to get started</p>
        </div>
      ) : (
        <div className="space-y-3">
          {codes.map(code => (
            <div
              key={code.id}
              className={`bg-[#121212] rounded-xl border p-4 ${
                code.used ? 'border-white/5 opacity-60' : 'border-white/10'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {/* Code */}
                  <div className="flex items-center gap-2">
                    <code className="text-lg font-mono font-bold text-white bg-white/5 px-3 py-1 rounded-lg">
                      {code.code}
                    </code>
                    <button
                      onClick={() => copyToClipboard(code.code)}
                      className="p-2 hover:bg-white/5 rounded-lg transition-colors"
                      title="Copy code"
                    >
                      {copiedCode === code.code ? (
                        <Check className="w-4 h-4 text-green-400" />
                      ) : (
                        <Copy className="w-4 h-4 text-neutral-400" />
                      )}
                    </button>
                  </div>

                  {/* Status Badge */}
                  {code.used ? (
                    <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                      <XCircle className="w-3 h-3 mr-1" />
                      Used
                    </Badge>
                  ) : (
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Available
                    </Badge>
                  )}

                  {/* Duration */}
                  <span className="text-sm text-neutral-400">
                    <Clock className="w-4 h-4 inline mr-1" />
                    {code.trial_duration_days} days
                  </span>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  {!code.used && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteCode(code.id, code.code)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Details */}
              <div className="mt-3 flex flex-wrap gap-4 text-xs text-neutral-500">
                <span>
                  <Calendar className="w-3 h-3 inline mr-1" />
                  Created: {format(parseISO(code.created_at), 'MMM d, yyyy HH:mm')}
                </span>
                
                {code.used && code.used_at && (
                  <span>
                    Used: {format(parseISO(code.used_at), 'MMM d, yyyy HH:mm')}
                  </span>
                )}
                
                {code.used_by_business && (
                  <span className="flex items-center gap-1">
                    <Building className="w-3 h-3" />
                    {code.used_by_business.business_name}
                    {code.used_by_business.handle && (
                      <span className="text-neutral-600">(@{code.used_by_business.handle})</span>
                    )}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminTrialCodes;
